import React, { ReactElement, useRef } from "react";
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import type { Json } from "@pragmatically-typed/body.json";
import type { ErrorOption, FieldErrors } from "react-hook-form";
import { logException } from "@wss/error-tracking";
import { useReCaptcha } from "@wss/re-captcha";
import { Schema } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import convertJsonToFormData from "./FormHelpers/convertJsonToFormData";
import { emptySchema } from "./validationSchema";
import { sendCustomerIoNewsletterSignupEvent } from "~/analytics/CustomerIo/EmailEvents";

interface Props<TSubmissionResponse, TFieldValues extends FieldValues> {
  children: ReactElement;
  formType?: string;
  validationSchema?: Schema<TFieldValues>;
  preValidationDataTransform?: (values: TFieldValues) => TFieldValues;
  onSubmitSuccess: (responseData: TSubmissionResponse) => void;
  onSubmitError: (
    responseData: TSubmissionResponse | "GeneralFailure",
    response: Response | undefined,
  ) => void;
  beforeSubmit?: () => void;
  recaptchaAction?: string;
  submissionUrl: string;
  parseResponse?: (response: Response) => Promise<TSubmissionResponse>;
  defaultValues?: DefaultValues<TFieldValues>;
  resetForm?: boolean;
  onFormSubmitInvalid?: (errors: FieldErrors<TFieldValues>) => void;
}

import(/* webpackChunkName: "form-styles" */ "./styles.css");

export function HelperSetDotNetErrors(
  errors: Record<string, string>,
  setErrorUseFormMethod: (name: string, error: ErrorOption) => void,
) {
  if (errors === undefined || errors === null) {
    return;
  }

  const validationErrorKeys = Object.keys(errors);
  for (let i = 0; i < validationErrorKeys.length; i += 1) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const key: any = validationErrorKeys[i];
    if (key !== undefined) {
      const error = errors[key];
      const dotNetFormatFieldName = key[0].toUpperCase() + key.slice(1);

      setErrorUseFormMethod(dotNetFormatFieldName, {
        message: error?.toString(),
        type: "validation",
      });
    }
  }
}

function ValidatedForm<
  TSubmissionResponse extends Json,
  TFieldValues extends FieldValues = FieldValues,
>({
  children,
  validationSchema,
  preValidationDataTransform,
  onSubmitSuccess,
  onSubmitError,
  beforeSubmit,
  formType,
  recaptchaAction,
  submissionUrl,
  parseResponse = (response: Response) => response.json<TSubmissionResponse>(),
  defaultValues,
  resetForm = true,
  onFormSubmitInvalid,
}: Props<TSubmissionResponse, TFieldValues>) {
  /**
   * Not all consumers of the component support reCaptcha.
   * For that reason we can not use useReCaptcha from @wss/re-captcha
   */
  const recaptcha = useReCaptcha();
  const fieldValues = useRef<FieldValues>();

  const formMethods = useForm<TFieldValues>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    resolver: (data, ctx, opts) => {
      const transform = preValidationDataTransform?.(data) || data;
      const resolver = zodResolver<TFieldValues>(
        validationSchema ?? emptySchema,
      );
      return resolver(transform, ctx, opts);
    },

    shouldFocusError: false,
    defaultValues,
  });

  const getSubmittedEmailInForm = (formData: FormData) => {
    return (
      formData.get("Email") ||
      formData.get("email") ||
      formData.get("EmailAddress") ||
      formData.get("emailAddress") ||
      formData.get("emailaddress") ||
      formData.get("contactCSEmail")
    )?.toString();
  };

  const updateFieldValuesAndRecaptcha: SubmitHandler<TFieldValues> = async (
    data,
  ) => {
    fieldValues.current = data;
    try {
      const result = await recaptcha.execute(recaptchaAction);
      if (result.isError) return submitForm(data);
      return submitForm(data, result.value);
    } catch (error) {
      return submitForm(data);
    }
  };

  const submitForm = async (data: FieldValues, token?: string) => {
    if (beforeSubmit) {
      beforeSubmit();
    }

    const formData = convertJsonToFormData(data);

    if (token) {
      formData.append("RecaptchaToken", token);
    }

    let response: Response | undefined;

    try {
      response = await fetch(submissionUrl, {
        method: "POST",
        body: formData,
      });

      const isSuccessful = response.status >= 200 && response.status < 300;
      const result = await parseResponse(response);

      if (isSuccessful) {
        if (resetForm) {
          formMethods.reset(defaultValues);
        }
        onSubmitSuccess(result);

        const email = getSubmittedEmailInForm(formData);
        const formDataAction = (() => {
          const value = formData.get("FormAction");
          return typeof value === "string" ? value.toLowerCase() : "";
        })();
        if (email && formType === "newsletter") {
          sendCustomerIoNewsletterSignupEvent(email);
        }
        if (email && formDataAction !== "survey") {
          const { emailSignupEvent } = await import("~/gtm");
          emailSignupEvent(email);
        }
      } else if (response.status >= 500) {
        onSubmitError("GeneralFailure", response);
      } else {
        onSubmitError(result, response);
      }
    } catch (error) {
      logException(error);
      onSubmitError("GeneralFailure", response);
    }
  };

  const onInvalidSubmit: SubmitErrorHandler<TFieldValues> = (
    errors: FieldErrors<TFieldValues>,
  ) => {
    const fieldNames = Object.keys(errors);
    if (fieldNames[0] === undefined) {
      return;
    }

    const querySelector = fieldNames.map((name) => `[name=${name}]`).join(",");
    const firstErrorFieldWrapper =
      document.querySelector(querySelector)?.parentElement;

    if (typeof firstErrorFieldWrapper?.scrollIntoView !== "function") {
      return;
    }

    firstErrorFieldWrapper.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });

    if (onFormSubmitInvalid) {
      onFormSubmitInvalid(errors);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(
          updateFieldValuesAndRecaptcha,
          onInvalidSubmit,
        )}
      >
        {children}
      </form>
    </FormProvider>
  );
}

export default ValidatedForm;
